import { useMemo, useState } from "react";
import { ClipboardList, Download, Loader2 } from "lucide-react";
import { AppLayout } from "@/components/layout/AppLayout";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useAuth } from "@/modules/auth/hooks/useAuth";
import { AuditSummaryCards } from "../components/AuditSummaryCards";
import { AuditFilters } from "../components/AuditFilters";
import { AuditEventRow } from "../components/AuditEventRow";
import { useAuditEvents, buildExportUrl } from "../hooks/useAudit";
import type { AuditCategory, AuditFilters as Filters } from "../types";

type Tab = "all" | "system" | "data" | "security" | "system_event";

const CARD_BORDER = "hsl(210,30%,85%)";

const TABS: { key: Tab; label: string; category?: AuditCategory }[] = [
  { key: "all", label: "All Events" },
  { key: "system", label: "System Activity", category: "SYSTEM_ACTIVITY" },
  { key: "data", label: "Data Changes", category: "DATA_CHANGE" },
  { key: "security", label: "Security Events", category: "SECURITY_EVENT" },
  { key: "system_event", label: "System Events", category: "SYSTEM_EVENT" },
];

export default function AuditCenterPage() {
  const { accessToken } = useAuth();
  const [tab, setTab] = useState<Tab>("all");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(5);
  const [filters, setFilters] = useState<Filters>({ page: 1, limit: 5 });
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const activeFilters: Filters = useMemo(() => {
    const tabDef = TABS.find((t) => t.key === tab);
    return {
      ...filters,
      category: tabDef?.category,
      page,
      limit,
    };
  }, [filters, tab, page, limit]);

  const { data, isLoading, isError } = useAuditEvents(activeFilters);

  function handleExport() {
    const url = buildExportUrl(activeFilters);
    const a = document.createElement("a");
    a.href = url;
    a.rel = "noopener";
    if (accessToken) {
      // pass via query-less anchor; API uses cookie auth too
    }
    a.click();
  }

  function resetFilters() {
    setFilters({ page: 1, limit });
    setPage(1);
  }

  const summary = data?.summary ?? {
    totalEvents: 0,
    systemActivity: 0,
    dataChanges: 0,
    securityEvents: 0,
    systemEvents: 0,
    failures: 0,
  };

  const events = data?.data ?? [];
  const total = data?.total ?? 0;
  const showSystemEventPlaceholder = tab === "system_event";

  return (
    <AppLayout
      breadcrumb={[
        { label: "PhazeX Hub", href: "/" },
        { label: "Administration" },
        { label: "Audit Center" },
      ]}
    >
      <div className="w-full space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <ClipboardList
              className="h-[18px] w-[18px] text-foreground"
              strokeWidth={1.5}
            />
            <h1 className="text-2xl font-semibold tracking-tight">
              Audit Center
            </h1>
          </div>
          <Button variant="outline" onClick={handleExport}>
            <Download className="h-4 w-4" strokeWidth={1.5} />
            Export CSV
          </Button>
        </div>

        <AuditSummaryCards summary={summary} />

        <div className="border-b border-border">
          <nav className="-mb-px flex gap-6">
            {TABS.map((t) => (
              <button
                key={t.key}
                type="button"
                onClick={() => {
                  setTab(t.key);
                  setPage(1);
                  setFilters((f) => ({ ...f, page: 1 }));
                  setExpandedId(null);
                }}
                className={cn(
                  "border-b-2 px-1 pb-3 text-sm font-medium transition-colors",
                  tab === t.key
                    ? "border-primary text-primary"
                    : "border-transparent text-muted-foreground hover:text-foreground",
                )}
              >
                {t.label}
              </button>
            ))}
          </nav>
        </div>

        <AuditFilters
          filters={filters}
          onChange={setFilters}
          onReset={resetFilters}
        />

        <div
          className="overflow-hidden rounded-xl border bg-card"
          style={{ borderColor: CARD_BORDER }}
        >
          {showSystemEventPlaceholder ? (
            <div className="py-16 text-center text-sm text-muted-foreground">
              Coming in V2
            </div>
          ) : isError ? (
            <div className="p-6 text-sm text-destructive">
              Failed to load audit events.
            </div>
          ) : isLoading ? (
            <div className="flex items-center justify-center py-16 text-sm text-muted-foreground">
              <Loader2
                className="mr-2 h-4 w-4 animate-spin"
                strokeWidth={1.5}
              />
              Loading events…
            </div>
          ) : events.length === 0 ? (
            <div className="py-16 text-center text-sm text-muted-foreground">
              No audit events match the current filters.
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-[hsl(210,30%,97%)] text-left text-xs font-medium uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th className="w-8 px-3 py-3"></th>
                  <th className="px-3 py-3">Time</th>
                  <th className="px-3 py-3">User</th>
                  <th className="px-3 py-3">Event</th>
                  <th className="px-3 py-3">Module</th>
                  <th className="px-3 py-3">Entity</th>
                  <th className="px-3 py-3">Type</th>
                  <th className="px-3 py-3">Severity</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {events.map((event) => (
                  <AuditEventRow
                    key={event.id}
                    event={event}
                    isExpanded={expandedId === event.id}
                    onToggle={() =>
                      setExpandedId((prev) =>
                        prev === event.id ? null : event.id,
                      )
                    }
                  />
                ))}
              </tbody>
            </table>
          )}

          {!showSystemEventPlaceholder && !isError && !isLoading && events.length > 0 && (
            <div data-pagination="true" style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '9px 14px',
              borderTop: '1px solid hsl(210,30%,85%)',
              background: 'hsl(210,30%,97%)',
              fontSize: '12px',
              color: 'hsl(215,16%,47%)',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <span>
                  Showing {total === 0 ? 0 : (page - 1) * limit + 1}–{Math.min(page * limit, total)} of {total} entries
                </span>
                <span style={{ marginLeft: '8px' }}>Show</span>
                <select
                  value={limit}
                  onChange={e => { setLimit(Number(e.target.value)); setPage(1) }}
                  style={{
                    height: '26px',
                    padding: '0 4px',
                    border: '1px solid hsl(210,30%,85%)',
                    borderRadius: '4px',
                    fontSize: '12px',
                    background: 'white',
                    cursor: 'pointer',
                  }}
                >
                  <option value={5}>5</option>
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                </select>
                <span>entries</span>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <button
                  onClick={() => setPage(p => p - 1)}
                  disabled={page === 1}
                  style={{
                    width: '28px', height: '28px',
                    border: '1px solid hsl(210,30%,85%)',
                    borderRadius: '4px',
                    background: 'white',
                    cursor: page === 1 ? 'not-allowed' : 'pointer',
                    opacity: page === 1 ? 0.4 : 1,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}
                >‹</button>
                <span style={{ padding: '0 10px' }}>
                  {Math.ceil(total / limit) === 0 ? '0' : page} / {Math.ceil(total / limit) || 1}
                </span>
                <button
                  onClick={() => setPage(p => p + 1)}
                  disabled={page >= Math.ceil(total / limit)}
                  style={{
                    width: '28px', height: '28px',
                    border: '1px solid hsl(210,30%,85%)',
                    borderRadius: '4px',
                    background: 'white',
                    cursor: page >= Math.ceil(total / limit) ? 'not-allowed' : 'pointer',
                    opacity: page >= Math.ceil(total / limit) ? 0.4 : 1,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}
                >›</button>
              </div>
            </div>
          )}
        </div>
      </div>
    </AppLayout>
  );
}
